import { isCancelledDeal, isOpenDeal, OrderForm, orderFormScore } from "../../../types/OrderFormTypes";
import { SpendCategory, VendorOrderForm } from "../../../types/VendorTypes";

/**
 * The vendor page's model: every deal of the org placed under a category and
 * a vendor, so the page can tally spend down the category tree, then by
 * vendor, then list the deals.
 *
 * A deal's category is the server's `spendCategory`: a path in the org's own
 * taxonomy when it has one, in the IL spend taxonomy otherwise. The matching
 * taxonomy orders the tree. Deals and vendors without a category share a
 * "not categorized" bucket.
 */

/** The key of the bucket for vendors and deals without a category. */
export const NO_CATEGORY = "";

/** Joins a path's labels into its key; labels never contain ">" (the server rejects them). */
const PATH_SEPARATOR = " > ";

export interface CategoryRef {
    key: string;
    /** Root first; undefined for the "not categorized" bucket. */
    path?: string[];
}

export const categoryRef = (path?: string[]): CategoryRef =>
    path?.length ? { key: path.join(PATH_SEPARATOR), path } : { key: NO_CATEGORY };

export const categoryFromKey = (key: string): CategoryRef =>
    key === NO_CATEGORY ? { key } : { key, path: key.split(PATH_SEPARATOR) };

/** Whether a category is the scope or lies under it; no scope takes everything. */
export const inCategory = (key: string, scopeKey?: string | null): boolean =>
    scopeKey == null || key === scopeKey || key.startsWith(scopeKey + PATH_SEPARATOR);

export interface Deal {
    deal: OrderForm;
    vendor: VendorOrderForm;
    category: CategoryRef;
}

export interface VendorDeals {
    vendor: VendorOrderForm;
    /** The categories the vendor has deals in, or the one bucket it sits in without deals. */
    categories: CategoryRef[];
    deals: Deal[];
}

export interface VendorSpendModel {
    taxonomy: SpendCategory[];
    vendors: VendorDeals[];
    deals: Deal[];
}

export interface SpendTally {
    vendors: number;
    deals: number;
    /** Sum of the deal values, in dollars. */
    value: number;
    /** Average score of the scored deals; undefined when none is scored. */
    score?: number;
    /** Sums in cents. Cancelled deals count in none of them. */
    targetedSavings: number;
    /** Realized on signed deals: banked. */
    realizedSavings: number;
    /** Realized so far on deals still open: on the table, not banked. */
    pendingSavings: number;
}

export interface CategorySpend extends CategoryRef, SpendTally {}

export interface VendorSpend extends SpendTally {
    vendor: VendorOrderForm;
    categories: CategoryRef[];
}

/** A category of the spend tree: its tally covers its whole subtree. */
export interface CategoryNode extends CategorySpend {
    kind: "category";
    /** Subcategories first, then the vendors with deals in this very category. */
    children: TreeNode[];
}

/** How the spend tree is rooted: at the category taxonomy, or straight at the vendors. */
export type SpendView = "category" | "vendor";

/**
 * A vendor of the spend tree: under a category, its tally covers its deals in
 * that category; as a root (the per-vendor view), its deals in the scope.
 */
export interface VendorNode extends VendorSpend {
    kind: "vendor";
    /** Unique in the tree, since a vendor can sit under several categories. */
    key: string;
    /** The category the vendor hangs under; unset when the vendor is a root. */
    category?: CategoryRef;
    /** The vendor's deals in this category, biggest savings target first. */
    children: DealNode[];
}

/** A deal under a vendor of the spend tree; its tally is the deal's own numbers. */
export interface DealNode extends Deal, SpendTally {
    kind: "deal";
    key: string;
}

export type TreeNode = CategoryNode | VendorNode | DealNode;

export function buildVendorSpend(
    vendors: VendorOrderForm[],
    taxonomy: SpendCategory[]
): VendorSpendModel {
    const vendorDeals = vendors.map((vendor): VendorDeals => {
        const deals = (vendor.orderForms ?? []).map((deal): Deal => ({
            deal,
            vendor,
            category: categoryRef(deal.spendCategory),
        }));
        const categories = distinctCategories(deals.map((d) => d.category));
        return { vendor, categories: categories.length ? categories : [categoryRef()], deals };
    });
    return { taxonomy, vendors: vendorDeals, deals: vendorDeals.flatMap((v) => v.deals) };
}

const distinctCategories = (refs: CategoryRef[]): CategoryRef[] => {
    const byKey = new Map(refs.map((ref) => [ref.key, ref]));
    return Array.from(byKey.values());
};

export function tally(deals: Deal[], vendors: number): SpendTally {
    const scores = deals
        .map((d) => orderFormScore(d.deal))
        .filter((s): s is number => s !== undefined);
    const pursued = deals.filter((d) => !isCancelledDeal(d.deal));
    const realizedOf = (ds: Deal[]) =>
        ds.reduce((sum, d) => sum + (Number(d.deal.realizedSavings) || 0), 0);
    return {
        vendors,
        deals: deals.length,
        value: deals.reduce((sum, d) => sum + (Number(d.deal.value) || 0), 0),
        score: scores.length ? scores.reduce((sum, s) => sum + s, 0) / scores.length : undefined,
        targetedSavings: pursued.reduce((sum, d) => sum + (Number(d.deal.targetedSavings) || 0), 0),
        realizedSavings: realizedOf(pursued.filter((d) => !isOpenDeal(d.deal))),
        pendingSavings: realizedOf(pursued.filter((d) => isOpenDeal(d.deal))),
    };
}

/** The vendors in a category's subtree (every vendor when no category is given). */
export function vendorsIn(model: VendorSpendModel, categoryKey?: string | null): VendorSpend[] {
    return model.vendors
        .filter((v) => v.categories.some((c) => inCategory(c.key, categoryKey)))
        .map((v) => {
            const deals = v.deals.filter((d) => inCategory(d.category.key, categoryKey));
            return { vendor: v.vendor, categories: v.categories, ...tally(deals, 1) };
        })
        .sort((a, b) => b.value - a.value);
}

/** The tally of a category's whole subtree. */
const tallyCategory = (model: VendorSpendModel, key: string): SpendTally =>
    tally(
        model.deals.filter((d) => inCategory(d.category.key, key)),
        model.vendors.filter((v) => v.categories.some((c) => inCategory(c.key, key))).length
    );

const bySavingsTarget = (a: Deal, b: Deal) =>
    (Number(b.deal.targetedSavings) || 0) - (Number(a.deal.targetedSavings) || 0);

const dealNode = (parentKey: string, d: Deal): DealNode => ({
    kind: "deal",
    key: `${parentKey} :: ${d.deal.id}`,
    ...d,
    ...tally([d], 1),
});

const vendorNode = (
    key: string,
    v: VendorDeals,
    deals: Deal[],
    category?: CategoryRef
): VendorNode => ({
    kind: "vendor",
    key,
    category,
    vendor: v.vendor,
    categories: v.categories,
    ...tally(deals, 1),
    children: deals.sort(bySavingsTarget).map((d) => dealNode(key, d)),
});

const bySpend = (a: SpendTally, b: SpendTally) => b.value - a.value;

/** The vendors sitting in this very category (not its subtree), biggest spend first. */
const vendorNodes = (model: VendorSpendModel, category: CategoryRef): VendorNode[] =>
    model.vendors
        .filter((v) => v.categories.some((c) => c.key === category.key))
        .map((v) =>
            vendorNode(
                `${category.key} :: ${v.vendor.vendorKey}`,
                v,
                v.deals.filter((d) => d.category.key === category.key),
                category
            )
        )
        .sort(bySpend);

/**
 * The per-vendor tree: the vendors of a category's subtree (every vendor when
 * no category is given) as roots, biggest spend first, each with its deals in
 * that scope under it.
 */
export function vendorTree(model: VendorSpendModel, categoryKey?: string | null): VendorNode[] {
    return model.vendors
        .filter((v) => v.categories.some((c) => inCategory(c.key, categoryKey)))
        .map((v) =>
            vendorNode(
                v.vendor.vendorKey,
                v,
                v.deals.filter((d) => inCategory(d.category.key, categoryKey))
            )
        )
        .sort(bySpend);
}

/**
 * The spend tree: the category taxonomy, pruned to the branches that hold a
 * vendor or a deal, each category tallying its whole subtree, with the vendors
 * as leaves under the categories they have deals in. Sibling categories follow
 * the taxonomy's order; categories the taxonomy no longer has come after, and
 * the "not categorized" bucket last.
 */
export function categoryTree(model: VendorSpendModel): CategoryNode[] {
    // Every path in use, plus each of its ancestors.
    const used = new Set<string>();
    for (const vendor of model.vendors) {
        for (const { path } of vendor.categories) {
            path?.forEach((_, i) => used.add(path.slice(0, i + 1).join(PATH_SEPARATOR)));
        }
    }
    const hasBucket = model.vendors.some((v) => v.categories.some((c) => c.key === NO_CATEGORY));

    const build = (
        parent: string[],
        taxonomyLevel: SpendCategory[] | undefined
    ): CategoryNode[] => {
        const prefix = parent.length ? parent.join(PATH_SEPARATOR) + PATH_SEPARATOR : "";
        const usedHere = Array.from(used)
            .filter(
                (key) =>
                    key.startsWith(prefix) && !key.slice(prefix.length).includes(PATH_SEPARATOR)
            )
            .map((key) => key.slice(prefix.length));
        const known = (taxonomyLevel ?? []).map((c) => c.label).filter((l) => usedHere.includes(l));
        const labels = [...known, ...usedHere.filter((l) => !known.includes(l))];
        return labels.map((label): CategoryNode => {
            const path = [...parent, label];
            const ref = categoryRef(path);
            const children = taxonomyLevel?.find((c) => c.label === label)?.children;
            return {
                kind: "category",
                ...ref,
                ...tallyCategory(model, ref.key),
                children: [...build(path, children), ...vendorNodes(model, ref)],
            };
        });
    };

    const roots = build([], model.taxonomy);
    const bucket = categoryRef();
    return hasBucket
        ? [
              ...roots,
              {
                  kind: "category",
                  ...bucket,
                  ...tallyCategory(model, NO_CATEGORY),
                  children: vendorNodes(model, bucket),
              },
          ]
        : roots;
}

/** The category with this key, anywhere in the tree. */
export function findCategory(nodes: TreeNode[], key: string): CategoryNode | undefined {
    for (const node of nodes) {
        if (node.kind !== "category") continue;
        if (node.key === key) return node;
        const found = findCategory(node.children, key);
        if (found) return found;
    }
    return undefined;
}

/** Every node of the tree, parents before children. */
export const flattenTree = (nodes: TreeNode[]): TreeNode[] =>
    nodes.flatMap((node) =>
        node.kind === "deal" ? [node] : [node, ...flattenTree(node.children)]
    );

/** The deals in a category's subtree and/or of a vendor, biggest savings target first. */
export function dealsIn(
    model: VendorSpendModel,
    categoryKey?: string | null,
    vendorKey?: string | null
): Deal[] {
    return model.deals
        .filter(
            (d) =>
                inCategory(d.category.key, categoryKey) &&
                (vendorKey == null || d.vendor.vendorKey === vendorKey)
        )
        .sort(bySavingsTarget);
}

/** The tally of the whole scope: all vendors, one category's subtree, or one vendor. */
export function scopeTally(
    model: VendorSpendModel,
    categoryKey?: string | null,
    vendorKey?: string | null
): SpendTally {
    const vendors = vendorsIn(model, categoryKey).filter(
        (v) => vendorKey == null || v.vendor.vendorKey === vendorKey
    );
    return tally(dealsIn(model, categoryKey, vendorKey), vendors.length);
}
